import React from 'react';
import { PREVIEW_PRICE_DISPLAY } from '../../config/pricing';
import { getRuntimeSiteContext } from '../../lib/siteContext';

const pluralize = (count, singular, plural) => (count === 1 ? singular : plural);

const ItineraryUpgradeCta = ({
  isTripCapped,
  plannedDays,
  totalTripDays,
  maxItineraryDays,
  daysShown,
  unplannedDays,
  onUnlock,
  unlockPrice = PREVIEW_PRICE_DISPLAY,
  isFreePilot = false,
  disabled = false,
}) => {
  const unlockableDays = Math.max(0, Math.min(plannedDays, maxItineraryDays) - daysShown);
  const hasLockedDays = unlockableDays > 0;
  const previewLabel = daysShown >= 2 ? 'Arrival Day & Day 1' : `${daysShown} preview ${pluralize(daysShown, 'day', 'days')}`;
  const totalLabel = plannedDays > 0 ? `${plannedDays}-day itinerary` : 'itinerary';
  const { islandConfig } = React.useMemo(() => getRuntimeSiteContext(), []);
  const unlockLine = hasLockedDays
    ? `Unlock the remaining ${unlockableDays} ${pluralize(unlockableDays, 'day', 'days')} and the ability to iterate on this plan to make it truly yours.`
    : `Unlock the complete ${totalLabel} and the ability to iterate on this plan to make it truly yours.`;

  return (
    <div className="rounded-lg glass-card p-4 md:p-6 mb-6 space-y-6">
      <div className="space-y-3">
        <h3 className="text-xl md:text-2xl font-bold text-white">
          {isFreePilot ? 'Your complete itinerary is free' : `Unlock the rest of your ${islandConfig.shortTitle} plan`}
        </h3>
        <p className="text-sm text-white leading-relaxed">
          {isFreePilot
            ? 'All planned days, itinerary edits, and exports are included during our 30-day pilot. No credit card required.'
            : <>You're previewing {previewLabel}. {unlockLine}</>}
        </p>
      </div>

      {isTripCapped && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex items-start gap-3">
            <span className="text-amber-300 text-xl mt-0.5">ℹ️</span>
            <div className="space-y-1">
              <div className="font-medium text-amber-100">Extended trip notice</div>
              <div className="text-sm text-amber-100/80 leading-relaxed">
                Your {totalTripDays}-day journey is longer than our {maxItineraryDays}-day planning window.
                You'll unlock a detailed plan for the first {maxItineraryDays} days right away.
                {unplannedDays > 0 && ` The final ${unplannedDays} ${pluralize(unplannedDays, 'day remains', 'days remain')} queued for future releases.`}
              </div>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onUnlock}
        disabled={disabled}
        className="w-full btn-primary-cta"
      >
        <span className="text-base md:text-lg font-semibold text-gray-900">{isFreePilot ? 'Get My Complete Itinerary Free' : unlockPrice}</span>
        {isTripCapped && (
          <span className="sr-only">Unlocks first {maxItineraryDays} days</span>
        )}
      </button>
    </div>
  );
};

export default ItineraryUpgradeCta;
